import React, { useState, useEffect } from 'react';
import { PromptStarter } from '@fluentui-copilot/react-prompt-starter';
import { OutputCard } from '@fluentui-copilot/react-output-card';
import { makeStyles, tokens, Body1 } from '@fluentui/react-components';
import { COPILOT_CONSTANTS } from '../constants';
import { getDynamicSuggestions } from './SuggestedPrompt';


// Import centralized interfaces
import type { WelcomePromptProps } from '../interfaces';

import { sanitizeHtml } from "../../../utils/htmlSanitizer";

/**
 * WelcomePrompt - Legacy Style Implementation
 * Reusable component that renders the Copilot welcome message with configurable suggestions
 * Matches the legacy botSuggestionMessage approach with modern FluentUI styling
 */
const useStyles = makeStyles({
  card: {
    rowGap: '12px',
    marginLeft: '5px',
    marginRight: '5px',
    marginTop: '10px',
  },
  prompts: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingHorizontalS,
  },
  promptHighlight: {
    color: tokens.colorBrandForegroundLink,
  },
  bubble: {
    background: '#fff',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '12px 16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    marginBottom: '12px',
    // Provide small left gap so box isn't flush with panel edge
    marginLeft: '6px',
    fontSize: '14px',
    lineHeight: '20px',
    color: '#323130',
    fontWeight: 400,
    width: 'calc(100% - 6px)',
    // Allow long content to wrap nicely
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  icon: {
    width: '32px',
    height: '32px',
    marginRight: '8px'
  },
  bubbleWithIcon: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px'
  },
  suggestionsContainer: {
    marginLeft: '6px',
    marginTop: '0px',
    width: 'calc(100% - 6px)'
  },
  suggestionsTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    marginBottom: '8px',
    paddingLeft: '4px',
    display: 'none' // Hide title as legacy shows suggestions inline with welcome message
  },
  suggestionsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  suggestionButton: {
    fontSize: '13px',
    padding: '12px 16px',
    height: 'auto',
    minHeight: '40px',
    textAlign: 'left',
    justifyContent: 'flex-start',
    backgroundColor: '#fff',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground1,
    borderRadius: '8px',
    cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
      color: tokens.colorNeutralForeground1,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)'
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed
    },
    // Legacy-style text wrapping
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: '1.4',
    fontWeight: 400
  }
});

const WelcomePrompt: React.FC<WelcomePromptProps> = ({
  message,
  botSuggestionMessage,
  onSuggestionClick,
  maxSuggestions = 6,
  className = '',
  style
}) => {
  const styles = useStyles();
  const welcomeText = message || COPILOT_CONSTANTS.MESSAGES.WELCOME;
  
  // Use state to hold dynamic suggestions that can update
  const [dynamicSuggestions, setDynamicSuggestions] = useState<string>(botSuggestionMessage || '[]');
  
  // Load suggestions dynamically from session storage when component mounts
  useEffect(() => {
    const loadSuggestions = () => {
      const latestSuggestions = getDynamicSuggestions();
      setDynamicSuggestions(latestSuggestions);
    };
    
    // Load immediately
    loadSuggestions();
    
    // Also set up an interval to check for updates (in case API completes after mount)
    const intervalId = setInterval(loadSuggestions, 1000);
    
    // Cleanup interval after 5 seconds (API should complete by then)
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
    }, 5000);
    
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  // Parse legacy botSuggestionMessage (JSON string array or object array)
  const parsePrompts = (): Array<{task?: string, questionText: string}> => {
    if (!dynamicSuggestions) return [];
    try {
      const parsed = JSON.parse(dynamicSuggestions);
      if (!Array.isArray(parsed)) return [];
      
      return parsed.slice(0, maxSuggestions).map(item => {
        // Handle both string format (legacy) and object format (new)
        if (typeof item === 'string') {
          return { questionText: item };
        } else if (typeof item === 'object' && item.questionText) {
          return { task: item.task, questionText: item.questionText };
        }
        return { questionText: String(item) };
      });
    } catch (error) {
      return [];
    }
  };

  const prompts = parsePrompts();

  const onClickPrompt = (questionText: string) => {
    if (onSuggestionClick) {
      onSuggestionClick(questionText);
    }
  };

  const renderPrompts = () =>
    prompts.map((prompt, i) => (
      <PromptStarter
        key={`prompt-${i}`}
        prompt={
          <div>
            {prompt.task && (
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {prompt.task}
              </div>
            )}
            <div style={{ fontSize: '14px', fontWeight: 400 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(prompt.questionText , "welcome-prompt-html")}} />
          </div>
        }
        onClick={() => onClickPrompt(prompt.questionText)}
        {...{} as any} // Legacy compatibility
      />
    ));

  return (
    <OutputCard className={`${styles.card} ${className}`} style={style}>
      <Body1>
        <p style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(welcomeText , "welcome-prompt-html-2")}} />
      </Body1>
      <div className={styles.prompts}>
        {renderPrompts()}
      </div>
    </OutputCard>
  );
};

export default WelcomePrompt;

// Generated by Copilot
// updated on: 2026-07-27 | 05 lines added/updated | [Auto Generated] | US 1280933 AC-3 (#12996): sanitize welcome prompt HTML via shared sanitizeHtml | Time Taken 2 min
